/**
 * 日別カレンダービュー
 *
 * 選択した1日をひとつながりの時間軸として表示する。
 * 連続スクロールカレンダーと同じグリッド・同じ日別データキャッシュを使うため、
 * スクロール表示との切り替えでデータを取り直さない。
 */

import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useEffect, useMemo } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { useAppStore } from '@/store/useAppStore'
import { useGarminStore } from '@/store/useGarminStore'

import { dayDataQueryKey, fetchDayData } from '../day-data'
import { useCalendarExtras } from '../use-calendar-extras'
import { useCalendarFilterStore } from '../use-calendar-filter'
import { useDayVisitSync } from '../use-day-visit-sync'
import { buildEventsFromDays, getDayBounds } from './calendar-event-builder'
import { CalendarFilterBar } from './calendar-filter-bar'
import { ContinuousCalendarGrid } from './continuous-calendar-grid'
import { SpendingSummary } from './spending-summary'
import { TripDayCards } from './trip-day-cards'
import { useCalendarSheets } from './use-calendar-sheets'

type Props = {
  selectedDate: Date
}

export const CalendarDayView = ({ selectedDate }: Props) => {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const { activities, fetchActivities } = useGarminStore()
  const { handleEventSelect, sheets } = useCalendarSheets()
  const hiddenTypes = useCalendarFilterStore((state) => state.hiddenTypes)

  const dateStr = format(selectedDate, 'yyyy-MM-dd')

  const { dayStart, dayEnd } = useMemo(() => getDayBounds(selectedDate), [selectedDate])
  const { workouts, todos } = useCalendarExtras({ rangeStart: dayStart, rangeEnd: dayEnd })

  useEffect(() => {
    if (hasFullAccess) {
      void fetchActivities(100)
    }
  }, [hasFullAccess, fetchActivities])

  // シート経由で更新・削除された Visit をキャッシュに反映
  useDayVisitSync()

  const { data: dayData, isPending } = useQuery({
    queryKey: dayDataQueryKey(dateStr, hasFullAccess),
    queryFn: () => fetchDayData(dateStr, { hasFullAccess }),
  })

  const events = useMemo(() => {
    const all = buildEventsFromDays(dayData ? [dayData] : [], { activities, workouts, todos }, dayStart, dayEnd)
    return all.filter((event) => !hiddenTypes.includes(event.props.type))
  }, [dayData, activities, workouts, todos, dayStart, dayEnd, hiddenTypes])

  if (isPending) {
    return (
      <div className="space-y-4 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="activity-timeline flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b">
        <CalendarFilterBar />
        {((dayData?.receipts.length ?? 0) > 0 || (dayData?.cardTransactions.length ?? 0) > 0) && (
          <div className="shrink-0 pr-2">
            <SpendingSummary receipts={dayData?.receipts ?? []} cardTransactions={dayData?.cardTransactions} />
          </div>
        )}
      </div>

      <TripDayCards date={dateStr} className="shrink-0 border-b px-2 py-2" />

      {/* 日を変えたら現在時刻／朝の位置から見せ直したいので、日付ごとに作り直す */}
      <ContinuousCalendarGrid
        key={dateStr}
        rangeStart={dayStart}
        dayCount={1}
        events={events}
        onEventSelect={handleEventSelect}
      />

      {sheets}
    </div>
  )
}
